import { z } from "zod"
import { ProductSchema } from "../pos"
import { LegacyCompatibleDiscountSchema } from "./discount"
import { PartActionUrgency } from "./part-action-urgency"
import { PartFunction } from "./part-function"
import { PartGrade } from "./part-grade"
import { ServiceBikePart } from "./service-bike-part"
import { ServiceAction } from "./service-part-action"

export interface ServicePartWithAllInfo
  extends EvaluatedPartDuringProperInspection,
    ServiceBikePart {
  /** Custom label for the recommended action (e.g., for damper service custom names) */
  recommendedActionLabel?: string
}

export const ProductChoiceSchema = z.object({
  id: z.guid(),
  comment: z.string().default(""),
  recommendedAlternative: ProductSchema,
  otherAlternatives: z.array(ProductSchema).default([]),
})

export const ProductDecisionsSchema = z.record(
  z.guid(), // productGroupId
  z.guid(), // chosenProductId
)

export type ProductDecisions = z.infer<typeof ProductDecisionsSchema>
export type ProductChoice = z.infer<typeof ProductChoiceSchema>

export const EvaluatedServiceBikePartSchema = z.object({
  id: z.guid(),
  // Null when the action needs no grade: see `PartGradeUtils.isGradeOptional`.
  grade: z.nativeEnum(PartGrade).nullable(),
  recommendedAction: z.nativeEnum(ServiceAction),
  laborPrice: z.number().min(0),
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable().optional(), // percentage, e.g. 10 = 10%
  products: z.array(ProductSchema).default([]),
  productChoices: z.array(ProductChoiceSchema).default([]),
  comment: z.string().nullable(),
  // Damper brand selection for damper parts (Framgaffel, Bakdämpare)
  damperBrandId: z.guid().nullable().optional(),
  requiresPhysicalWorkshop: z.boolean().optional(),
  // When true, the customer cannot decline this part-action in the proposal
  // (pre-selected and locked in the marketplace).
  isMandatory: z.boolean().default(false),
  // Whether the work must be done now ("Trafiksäker") or could wait six months
  // ("Toppskick"). Defaulted so receptions and older drafts still parse.
  urgency: z.nativeEnum(PartActionUrgency).default(PartActionUrgency.NOW),
  // Whether the component does its job, apart from how worn it is. Defaulted
  // so older drafts and clients still parse.
  partFunction: z.nativeEnum(PartFunction).default(PartFunction.WORKS),
})

export const EvaluatedPartDuringProperInspectionSchema =
  EvaluatedServiceBikePartSchema.extend({
    requiredWashId: z.guid().nullable(),
    requiredPartIds: z.array(z.guid()),
    requiredManualServiceActionIds: z.array(z.guid()),
    requiresPhysicalWorkshop: z.boolean().default(false),
    // Doesn't count towards quality: set for every component at once, or a
    // package's work the mechanic never graded.
    isMassGraded: z.boolean().default(false),
  })

/**
 * A component as the reception saves it. The reception never grades
 * components; the mechanic does that during the inspection.
 */
export const ReceptionEvaluatedPartSchema = EvaluatedServiceBikePartSchema.omit(
  { grade: true, partFunction: true },
)

export type ReceptionEvaluatedPart = z.infer<
  typeof ReceptionEvaluatedPartSchema
>

/**
 * A component whose grade may still be missing: straight from the reception,
 * or in an inspection draft before the mechanic graded it.
 */
export type WithOptionalGrade<T extends { grade: PartGrade | null }> = Omit<
  T,
  "grade"
> & { grade: PartGrade | null }

/**
 * A draft component may also lack an action: one the mechanic only marked as
 * not working keeps that mark until they pick one.
 */
export type WithOptionalGradeAndAction<
  T extends { grade: PartGrade | null; recommendedAction: ServiceAction },
> = Omit<T, "grade" | "recommendedAction"> & {
  grade: PartGrade | null
  recommendedAction: ServiceAction | null
}

export type EvaluatedServiceBikePart = z.infer<
  typeof EvaluatedServiceBikePartSchema
>
export type EvaluatedPartDuringProperInspection = z.infer<
  typeof EvaluatedPartDuringProperInspectionSchema
>
